import { useState, useEffect } from 'react'
import { getCurrentQueryParamsStr } from './useAppAuth'
import { useUserInfo } from '../../components/Authentication/hooks'
import { useNavigate } from 'react-router-dom'

/**
 * Custom hook that determines the root redirect path based on auth status
 * Returns the path to redirect to, or null while checking
 */
export function useRootRedirectPath() {
  const [redirectPath, setRedirectPath] = useState(null)
  const { isSignedIn, isLoading } = useUserInfo()

  useEffect(() => {
    async function determineRedirectPath() {
      try {
        // Don't redirect while authentication is still loading
        if (isLoading) {
          setRedirectPath(null)
          return
        }

        // If we have a user in Redux, go to gallery
        if (isSignedIn) {
          setRedirectPath('/gallery')
          return
        }

        // Otherwise, validate session
        setRedirectPath('/auth')
      } catch (err) {
        console.log(
          'Unable to get a valid active user session:',
          err.message || err
        )
        setRedirectPath(`/auth${getCurrentQueryParamsStr()}`)
      }
    }

    determineRedirectPath()
  }, [isSignedIn, isLoading])

  return redirectPath
}

/**
 * Custom hook for route protection.
 * Navigates to the '/auth' page if not authenticated.
 */
export const useRouteProtection = () => {
  const { isSignedIn, isLoading } = useUserInfo()
  const navigate = useNavigate()

  useEffect(() => {
    // Don't redirect while authentication is still loading
    if (isLoading) {
      return
    }

    if (!isSignedIn) {
      // make sure we preserve the 'mode' query param,
      // if used explicitly by a Clerk redirect
      navigate(`/auth${getCurrentQueryParamsStr()}`)
    }
  }, [isSignedIn, isLoading, navigate])
}

export default useRouteProtection
